import { auth } from '../firebase';
import { getAuth } from 'firebase/auth';

// APIのベースURL
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:7071';

// 認証トークンを取得する関数
const getAuthToken = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('ユーザーが認証されていません');
  }
  return user.getIdToken();
};

// 管理者用API
export const getUsers = async () => {
  const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await getAuthToken()}`
    }
  });

  if (!response.ok) {
    throw new Error('ユーザー一覧の取得に失敗しました');
  }

  return response.json();
};

// 管理者用API
export const getOwnerUsers = async () => {
  const response = await fetch(`${API_BASE_URL}/api/admin/users/owner`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await getAuthToken()}`
    }
  });

  if (!response.ok) {
    throw new Error('ユーザー一覧の取得に失敗しました');
  }

  return response.json();
};

// 一般ユーザー用API
export const getUserDetail = async (userId) => {
  const user = auth.currentUser;
  if (!user) throw new Error('認証が必要です');
  const response = await fetch(`${API_BASE_URL}/api/users/${userId}`, {
    headers: {
      'Authorization': `Bearer ${await user.getIdToken()}`
    }
  });
  if (!response.ok) {
    throw new Error('ユーザー詳細の取得に失敗しました');
  }
  return response.json();
};

export const getPersonalDetail = async (userId) => {
  const user = auth.currentUser;
  if (!user) throw new Error('認証が必要です');
  const response = await fetch(`${API_BASE_URL}/api/users/${userId}/personal`, {
    headers: {
      'Authorization': `Bearer ${await user.getIdToken()}`
    }
  });
  if (!response.ok) {
    throw new Error('ユーザー詳細の取得に失敗しました');
  }
  return response.json();
};

// 管理者用API（memoを含む）
export const getAdminUserDetail = async (userId) => {
  const user = auth.currentUser;
  if (!user) throw new Error('認証が必要です');
  const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
    headers: {
      'Authorization': `Bearer ${await user.getIdToken()}`
    }
  });
  if (!response.ok) {
    throw new Error('ユーザー詳細の取得に失敗しました');
  }
  return response.json();
};

export const getAdminPersonalDetail = async (userId) => {
  const user = auth.currentUser;
  if (!user) throw new Error('認証が必要です');
  const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/personal`, {
    headers: {
      'Authorization': `Bearer ${await user.getIdToken()}`
    }
  });
  if (!response.ok) {
    throw new Error('ユーザー詳細の取得に失敗しました');
  }
  return response.json();
};

export const getUIDNameTable = async () => {
  const response = await fetch(`${API_BASE_URL}/api/admin/uid_table`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${await getAuthToken()}`
    }
  });
  return response.json();
};

export const getUserPreferences = async (userId) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/user/${userId}/preferences`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await getAuthToken()}`
    }
  });

  if (!response.ok) {
    throw new Error('ユーザーの設定情報の取得に失敗しました');
  }

  return response.json();
};

// 設備設定を取得する関数
export const getEquipmentConfig = async () => {
  const response = await fetch(`${API_BASE_URL}/api/equipment/config`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await getAuthToken()}`
    }
  });

  if (!response.ok) {
    throw new Error('設備設定の取得に失敗しました');
  }

  return response.json();
};

export const updateUser = async (userId, userData) => {
  const auth = getAuth();
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('認証が必要です');

  const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await currentUser.getIdToken()}`
    },
    body: JSON.stringify(userData)
  });

  if (!response.ok) {
    throw new Error('更新に失敗しました');
  }

  return response.json();
};

export const updatePersonal = async (userId, userData) => {
  const auth = getAuth();
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('認証が必要です');

  const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/personal`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await currentUser.getIdToken()}`
    },
    body: JSON.stringify(userData)
  });

  if (!response.ok) {
    throw new Error('更新に失敗しました');
  }

  return response.json();
};

// チャット関連のAPI
export const getChatMessages = async (itemType, targetUserId) => {
  const response = await fetch(
    `${API_BASE_URL}/api/chat/messages/${itemType}/${targetUserId}`,
    {
      headers: {
        'Authorization': `Bearer ${await getAuthToken()}`
      }
    }
  );

  if (!response.ok) {
    throw new Error('メッセージの取得に失敗しました');
  }

  return response.json();
};

export const sendChatMessage = async (targetUserId, itemType, message) => {
  const auth = getAuth();
  const currentUser = auth.currentUser;
  const response = await fetch(`${API_BASE_URL}/api/chat/messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await getAuthToken()}`
    },
    body: JSON.stringify({
      item_type: itemType,
      message: message.trim(),
      target_user_id: targetUserId,
      from_user_id: currentUser.uid
    })
  });

  if (!response.ok) {
    throw new Error('メッセージの送信に失敗しました');
  }

  return response.json();
};

// ユーザー設備画像アップロードAPI
export const uploadUserEquipmentImage = async (itemType, imageFile, user, targetUserId, description = '') => {
  const formData = new FormData();
  formData.append('file', imageFile);
  formData.append('description', description);
  const headers = { 'Authorization': `Bearer ${await user.getIdToken()}` };
  if (targetUserId) headers['X-Target-User-Id'] = targetUserId;
  const res = await fetch(`${API_BASE_URL}/api/user_equipment/${itemType}/upload_image`, {
    method: 'POST',
    headers,
    body: formData,
  });
  if (!res.ok) throw new Error('画像のアップロードに失敗しました');
  return await res.json();
};

// 来店予約API呼び出し
export const postVisitReservation = async (data) => {
  const user = auth.currentUser;
  const headers = { 'Content-Type': 'application/json' };
  if (user) headers['Authorization'] = `Bearer ${await user.getIdToken()}`;
  const res = await fetch(`${API_BASE_URL}/api/visit_reservations`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('来店予約の送信に失敗しました');
  return await res.json();
};

// サイン画像アップロードAPI
export const uploadSignatureImage = async (userId, itemType, signatureBlob) => {
  const user = auth.currentUser;
  const formData = new FormData();
  formData.append('file', signatureBlob, `${userId}_${itemType}_${Date.now()}.png`);
  const headers = {};
  if (user) headers['Authorization'] = `Bearer ${await user.getIdToken()}`;
  const res = await fetch(`${API_BASE_URL}/api/signature/upload`, {
    method: 'POST',
    headers,
    body: formData,
  });
  if (!res.ok) throw new Error('サイン画像のアップロードに失敗しました');
  return await res.json();
};

// UserItemPreferenceのロック情報を保存（UPSERT）
export const lockUserItemPreference = async (data) => {
  const user = auth.currentUser;
  const headers = { 'Content-Type': 'application/json' };
  if (user) headers['Authorization'] = `Bearer ${await user.getIdToken()}`;
  const res = await fetch(`${API_BASE_URL}/api/user_item_preferences`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('UserItemPreferenceの保存に失敗しました');
  return await res.json();
};

// 設備アイテムのロックAPI
export const lockEquipmentItem = async (userId, itemType, itemId, signatureUrl) => {
  const user = auth.currentUser;
  const headers = { 'Content-Type': 'application/json' };
  if (user) headers['Authorization'] = `Bearer ${await user.getIdToken()}`;
  const res = await fetch(`${API_BASE_URL}/api/equipment/lock`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ user_id: userId, item_type: itemType, item_id: itemId, signature_url: signatureUrl }),
  });
  if (!res.ok) throw new Error('設備アイテムのロックに失敗しました');
  return await res.json();
};

// --- UserItemPreferences ---
export const getUserItemPreferences = async (targetUserId) => {
  const user = auth.currentUser;
  const headers = { 'Authorization': `Bearer ${await user.getIdToken()}` };
  const endpoint = targetUserId
    ? `/api/admin/user/${targetUserId}/preferences`
    : '/api/user_item_preferences';
  const res = await fetch(`${API_BASE_URL}${endpoint}`, { headers });
  if (!res.ok) throw new Error('UserItemPreferences取得に失敗しました');
  return await res.json();
};

export const getMasterEquipmentDefinitions = async () => {
  const user = auth.currentUser;
  const headers = user ? { 'Authorization': `Bearer ${await user.getIdToken()}` } : {};
  const res = await fetch(`${API_BASE_URL}/api/master/equipment_definitions`, { headers });
  if (!res.ok) throw new Error('設備定義の取得に失敗しました');
  return await res.json();
};

export const getMasterEquipment = async (equipmentType) => {
  const user = auth.currentUser;
  const headers = user ? { 'Authorization': `Bearer ${await user.getIdToken()}` } : {};
  const res = await fetch(`${API_BASE_URL}/api/master/${equipmentType}`, { headers });
  if (!res.ok) throw new Error('設備データの取得に失敗しました');
  return await res.json();
};

export const createMasterEquipment = async (equipmentType, data) => {
  const user = auth.currentUser;
  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${await user.getIdToken()}` };
  const res = await fetch(`${API_BASE_URL}/api/master/${equipmentType}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error('マスタ設備の作成に失敗しました');
  return await res.json();
};

export const updateMasterEquipment = async (equipmentType, equipmentId, data) => {
  const user = auth.currentUser;
  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${await user.getIdToken()}` };
  const res = await fetch(`${API_BASE_URL}/api/master/${equipmentType}/${equipmentId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error('マスタ設備の更新に失敗しました');
  return await res.json();
};

export const deleteMasterEquipment = async (equipmentType, equipmentId) => {
  const user = auth.currentUser;
  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${await user.getIdToken()}` };
  const res = await fetch(`${API_BASE_URL}/api/master/${equipmentType}/${equipmentId}`, {
    method: 'DELETE',
    headers
  });
  if (!res.ok) throw new Error('マスタ設備の削除に失敗しました');
  return await res.json();
};

export const bulkDeleteMasterEquipment = async (equipmentType, equipmentIds) => {
  const user = auth.currentUser;
  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${await user.getIdToken()}` };
  const res = await fetch(`${API_BASE_URL}/api/master/${equipmentType}/bulk_delete`, {
    method: 'DELETE',
    headers,
    body: JSON.stringify(equipmentIds)
  });
  if (!res.ok) throw new Error('マスタ設備の一括削除に失敗しました');
  return await res.json();
};

export const applyDefaultQuantityToAllUsers = async (equipmentType, equipmentId, defaultQuantity) => {
  const user = auth.currentUser;
  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${await user.getIdToken()}` };
  const res = await fetch(`${API_BASE_URL}/api/master/${equipmentType}/${equipmentId}/apply-default-quantity`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ default_quantity: defaultQuantity })
  });
  if (!res.ok) throw new Error('全ユーザへのデフォルト数量適用に失敗しました');
  return await res.json();
};

export const uploadMasterEquipmentImage = async (file, description = '') => {
  const user = auth.currentUser;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('description', description);
  const headers = { 'Authorization': `Bearer ${await user.getIdToken()}` };
  const res = await fetch(`${API_BASE_URL}/api/master-upload_image`, {
    method: 'POST',
    headers,
    body: formData,
  });
  if (!res.ok) throw new Error('マスタ設備の画像のアップロードに失敗しました');
  return await res.json();
};

export const getUserEquipment = async (equipmentType, targetUserId) => {
  const user = auth.currentUser;
  const headers = { 'Authorization': `Bearer ${await user.getIdToken()}` };
  if (targetUserId) {
    headers['X-Target-User-Id'] = targetUserId;
  }
  const res = await fetch(`${API_BASE_URL}/api/user_equipment/${equipmentType}`, { headers });
  if (!res.ok) throw new Error('ユーザー登録設備の取得に失敗しました');
  return await res.json();
};

export const createUserEquipment = async (equipmentType, data) => {
  const user = auth.currentUser
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${await user.getIdToken()}`
  };
  const res = await fetch(`${API_BASE_URL}/api/user_equipment/${equipmentType}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error('設備の登録に失敗しました');
  return await res.json();
};

export const updateUserEquipment = async (equipmentType, equipmentId, data) => {
  const user = auth.currentUser
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${await user.getIdToken()}`
  };
  const res = await fetch(`${API_BASE_URL}/api/user_equipment/${equipmentType}/${equipmentId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error('設備の更新に失敗しました');
  return await res.json();
};

export const deleteUserEquipment = async (equipmentType, equipmentId, targetUserId) => {
  const user = auth.currentUser
  const headers = {
    'Authorization': `Bearer ${await user.getIdToken()}`,
    'X-Target-User-Id': targetUserId
  };
  const res = await fetch(`${API_BASE_URL}/api/user_equipment/${equipmentType}/${equipmentId}`, {
    method: 'DELETE',
    headers
  });
  if (!res.ok) throw new Error('設備の削除に失敗しました');
  return await res.json();
};

// UserItemPreference更新
export const updateUserItemPreference = async (preferenceId, data, user) => {
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${await user.getIdToken()}`
  };
  const res = await fetch(`${API_BASE_URL}/api/user_item_preferences/${preferenceId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error('UserItemPreferenceの更新に失敗しました');
  return await res.json();
};

// UserItemPreference新規作成
export const createUserItemPreference = async (data, user) => {
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${await user.getIdToken()}`
  };
  const res = await fetch(`${API_BASE_URL}/api/user_item_preferences`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error('UserItemPreferenceの作成に失敗しました');
  return await res.json();
};

// UserItemPreference資料ファイルアップロードAPI
export const uploadUserItemPreferenceMaterial = async (itemType, itemId, file, user, targetUserId) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('item_type', itemType);
  formData.append('item_id', itemId);
  if (targetUserId) formData.append('target_user_id', targetUserId);

  const headers = { 'Authorization': `Bearer ${await user.getIdToken()}` };
  const res = await fetch(`${API_BASE_URL}/api/user_item_preferences/upload_material`, {
    method: 'POST',
    headers,
    body: formData,
  });
  if (!res.ok) throw new Error('資料ファイルのアップロードに失敗しました');
  return await res.json();
};

// ユーザープロフィール取得API
export const getUserProfile = async (userId) => {
  const user = auth.currentUser;
  if (!user) throw new Error('認証が必要です');
  const res = await fetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
    headers: {
      'Authorization': `Bearer ${await user.getIdToken()}`
    }
  });
  if (!res.ok) throw new Error('ユーザープロフィールの取得に失敗しました');
  return await res.json();
};

// ユーザープロフィール更新API（name, phone, addressのみ）
export const updateUserProfile = async (userId, { name, phone, address }) => {
  const user = auth.currentUser;
  if (!user) throw new Error('認証が必要です');
  const res = await fetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await user.getIdToken()}`
    },
    body: JSON.stringify({ name, phone, address })
  });
  if (!res.ok) throw new Error('ユーザープロフィールの更新に失敗しました');
  return await res.json();
};

// お気に入り画像（like_images）API
export const getLikeImages = async (userId) => {
  const user = auth.currentUser;
  const headers = { 'Authorization': `Bearer ${await user.getIdToken()}` };
  const url = userId ? `${API_BASE_URL}/api/like_images?user_id=${userId}` : `${API_BASE_URL}/api/like_images`;
  const res = await fetch(url, { headers });
  if (!res.ok) throw new Error('画像一覧の取得に失敗しました');
  return await res.json();
};

export const uploadLikeImage = async (file, memo, userId) => {
  const user = auth.currentUser;
  const formData = new FormData();
  formData.append('file', file);
  if (memo) formData.append('memo', memo);
  if (userId) formData.append('user_id', userId);
  const headers = { 'Authorization': `Bearer ${await user.getIdToken()}` };
  const res = await fetch(`${API_BASE_URL}/api/like_images`, {
    method: 'POST',
    headers,
    body: formData,
  });
  if (!res.ok) throw new Error('画像のアップロードに失敗しました');
  return await res.json();
};

export const updateLikeImageMemo = async (imageId, memo, userId) => {
  const user = auth.currentUser;
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${await user.getIdToken()}`
  };
  const body = { memo };
  if (userId) body.user_id = userId;
  const res = await fetch(`${API_BASE_URL}/api/like_images/${imageId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('メモの更新に失敗しました');
  return await res.json();
};

export const deleteLikeImage = async (imageId, userId) => {
  const user = auth.currentUser;
  const headers = { 'Authorization': `Bearer ${await user.getIdToken()}` };
  let url = `${API_BASE_URL}/api/like_images/${imageId}`;
  if (userId) url += `?user_id=${userId}`;
  const res = await fetch(url, { method: 'DELETE', headers });
  if (!res.ok) throw new Error('画像の削除に失敗しました');
  return await res.json();
}; 

// ユーザ招待API
export const inviteUserByEmail = async (email) => {
  const user = auth.currentUser;
  if (!user) throw new Error('認証が必要です');
  const res = await fetch(`${API_BASE_URL}/api/user_invite`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await user.getIdToken()}`
    },
    body: JSON.stringify({ invitee_email: email })
  });
  if (!res.ok) throw new Error(await res.text());
  return await res.json();
};

export const getMyInvitations = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('認証が必要です');
  const res = await fetch(`${API_BASE_URL}/api/user_invite/list`, {
    headers: { 'Authorization': `Bearer ${await user.getIdToken()}` }
  });
  if (!res.ok) throw new Error(await res.text());
  return await res.json();
};

export const deleteAcceptedInvitation = async (inviteId) => {
  const user = auth.currentUser;
  if (!user) throw new Error('認証が必要です');
  const res = await fetch(`${API_BASE_URL}/api/user_invite/delete_accepted/${inviteId}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${await user.getIdToken()}` }
  });
  if (!res.ok) throw new Error(await res.text());
};

export const deletePendingInvitation = async (inviteId) => {
  const user = auth.currentUser;
  if (!user) throw new Error('認証が必要です');
  const res = await fetch(`${API_BASE_URL}/api/user_invite/delete_pending/${inviteId}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${await user.getIdToken()}` }
  });
  if (!res.ok) throw new Error(await res.text());
};

export const getMyLinkedUsers = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('認証が必要です');
  const res = await fetch(`${API_BASE_URL}/api/user_linked_accounts`, {
    headers: { 'Authorization': `Bearer ${await user.getIdToken()}` }
  });
  if (!res.ok) throw new Error(await res.text());
  return await res.json();
};

export const getLinkedToMe = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('認証が必要です');
  const res = await fetch(`${API_BASE_URL}/api/user_linked_to_me`, {
    headers: { 'Authorization': `Bearer ${await user.getIdToken()}` }
  });
  if (!res.ok) throw new Error(await res.text());
  return await res.json();
};

export const acceptInvitation = async (token) => {
  const res = await fetch(`${API_BASE_URL}/api/user_invite/accept?token=${token}`);
  if (!res.ok) throw new Error(await res.text());
  return await res.json();
};

// マスタ設備一括アップロードAPI
export const bulkUpdateMasterEquipment = async (equipmentType, file) => {
  const user = auth.currentUser;
  const formData = new FormData();
  formData.append('file', file);
  const headers = { 'Authorization': `Bearer ${await user.getIdToken()}` };
  const res = await fetch(`${API_BASE_URL}/api/master/${equipmentType}/bulk_update`, {
    method: 'POST',
    headers,
    body: formData,
  });
  if (!res.ok) throw new Error('一括アップロードに失敗しました');
  return await res.json();
};

// マスタ設備のitemtype一括変更API
export const bulkChangeEquipmentType = async (equipmentType, equipmentIds, newEquipmentType) => {
  const user = auth.currentUser;
  const headers = { 
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${await user.getIdToken()}` 
  };
  const res = await fetch(`${API_BASE_URL}/api/master/${equipmentType}/bulk_change_type`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      equipment_ids: equipmentIds,
      new_equipment_type: newEquipmentType
    }),
  });
  if (!res.ok) throw new Error('設備タイプの一括変更に失敗しました');
  return await res.json();
};

// 管理者権限の確認
export const checkAdminStatus = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('認証が必要です');
  const res = await fetch(`${API_BASE_URL}/api/admin/check`, {
    headers: { 'Authorization': `Bearer ${await user.getIdToken()}` }
  });
  if (!res.ok) throw new Error('管理者権限の確認に失敗しました');
  return await res.json();
};

export const getUnreadItemTypes = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('認証が必要です');
  const res = await fetch(`${API_BASE_URL}/api/chat/unread_item_types`, {
    headers: { 'Authorization': `Bearer ${await user.getIdToken()}` }
  });
  if (!res.ok) throw new Error('未読の設備種別一覧の取得に失敗しました');
  return await res.json();
};

export const getUnreadItemTypesByUserId = async (targetUserId) => {
  const user = auth.currentUser;
  if (!user) throw new Error('認証が必要です');
  const res = await fetch(`${API_BASE_URL}/api/admin/chat/unread_item_types/${targetUserId}`, {
    headers: { 'Authorization': `Bearer ${await user.getIdToken()}` }
  });
  if (!res.ok) throw new Error('未読の設備種別一覧の取得に失敗しました');
  return await res.json();
};

export const readChat = async (itemType, targetUserId) => {
  const user = auth.currentUser;
  if (!user) throw new Error('認証が必要です');
  const res = await fetch(`${API_BASE_URL}/api/chat/read/${itemType}`, {
    method: 'POST',
    headers: { 
      'Authorization': `Bearer ${await user.getIdToken()}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      target_user_id: targetUserId
    })
  });
  if (!res.ok) throw new Error('チャットの既読に失敗しました');
  return await res.json();
};

export const getUnreadUserIds = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('認証が必要です');
  const res = await fetch(`${API_BASE_URL}/api/admin/chat/unread_user_ids`, {
    headers: { 'Authorization': `Bearer ${await user.getIdToken()}` }
  });
  if (!res.ok) throw new Error('未読のユーザー一覧の取得に失敗しました');
  return await res.json();
};

export const migrateAdminUsers = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('認証が必要です');
  const res = await fetch(`${API_BASE_URL}/api/admin/migrate-admin-users`, {
    headers: { 'Authorization': `Bearer ${await user.getIdToken()}` }
  });
  if (!res.ok) throw new Error('管理者権限の移行に失敗しました');
  return await res.json();
};

export const getTenantInfo = async () => {
  const res = await fetch(`${API_BASE_URL}/api/tenant/info`);
  if (!res.ok) throw new Error('テナントの取得に失敗しました');
  return await res.json();
};

export const getTenantUsers = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('認証が必要です');
  const res = await fetch(`${API_BASE_URL}/api/admin/tenants`, {
    headers: { 'Authorization': `Bearer ${await user.getIdToken()}` }
  });
  if (!res.ok) throw new Error('テナントのユーザー一覧の取得に失敗しました');
  return await res.json();
};

// テナントのequipment schema更新API
export const updateTenantEquipmentSchema = async (tenantId, schemaData) => {
  const user = auth.currentUser;
  if (!user) throw new Error('認証が必要です');
  const res = await fetch(`${API_BASE_URL}/api/admin/tenant/${tenantId}/schema`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await user.getIdToken()}`
    },
    body: JSON.stringify(schemaData)
  });
  if (!res.ok) throw new Error('設備スキーマの更新に失敗しました');
  return await res.json();
};

// スキーマキャッシュリロードAPI
export const reloadSchemaCache = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('認証が必要です');
  const res = await fetch(`${API_BASE_URL}/api/admin/schema/reload`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await user.getIdToken()}`
    }
  });
  if (!res.ok) throw new Error('スキーマキャッシュのリロードに失敗しました');
  return await res.json();
};

// 確定ExcelアップロードAPI
export const uploadConfirmedExcel = async (file) => {
  const user = auth.currentUser;
  const formData = new FormData();
  formData.append('file', file, `confirmed_${Date.now()}.xlsx`);
  const headers = {};
  if (user) headers['Authorization'] = `Bearer ${await user.getIdToken()}`;
  const res = await fetch(`${API_BASE_URL}/api/confirmed_excel/upload`, {
    method: 'POST',
    headers,
    body: formData,
  });
  if (!res.ok) throw new Error('確定Excelのアップロードに失敗しました');
  return await res.json();
};

// 確定ExcelダウンロードAPI
export const downloadConfirmedExcel = async () => {
  const user = auth.currentUser;
  const headers = {};
  if (user) headers['Authorization'] = `Bearer ${await user.getIdToken()}`;
  const res = await fetch(`${API_BASE_URL}/api/confirmed_excel/download`, {
    method: 'GET',
    headers,
  });
  if (!res.ok) throw new Error('確定Excelのダウンロードに失敗しました');
  const blob = await res.blob();
  return blob;
};

// 確定Excel存在確認API
export const existsConfirmedExcel = async () => {
  const user = auth.currentUser;
  const headers = {};
  if (user) headers['Authorization'] = `Bearer ${await user.getIdToken()}`;
  const res = await fetch(`${API_BASE_URL}/api/confirmed_excel/exists`, {
    method: 'GET',
    headers,
  });
  if (!res.ok) throw new Error('確定Excelの存在確認に失敗しました');
  const json = await res.json();
  return json.exists;
};

// 管理者用: 確定済みExcelファイル削除API
export const clearConfirmedExcel = async (targetUserId = null) => {
  const user = auth.currentUser;
  const headers = {};
  if (user) headers['Authorization'] = `Bearer ${await user.getIdToken()}`;
  let url = `${API_BASE_URL}/api/confirmed_excel/clear`;
  if (targetUserId) url += `?user_id=${encodeURIComponent(targetUserId)}`;
  const res = await fetch(url, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok) throw new Error('確定Excelのクリアに失敗しました');
  return true;
};

// --- 見積もり外項目API ---

// 見積もり外項目一覧取得
export const getExcludedItems = async () => {
  const user = auth.currentUser;
  const headers = {};
  if (user) headers['Authorization'] = `Bearer ${await user.getIdToken()}`;
  
  const res = await fetch(`${API_BASE_URL}/api/excluded-items`, {
    method: 'GET',
    headers,
  });
  
  if (!res.ok) throw new Error('見積もり外項目の取得に失敗しました');
  return res.json();
};

// 見積もり外項目作成（管理者用）
export const createExcludedItem = async (item) => {
  const user = auth.currentUser;
  const headers = {
    'Content-Type': 'application/json',
  };
  if (user) headers['Authorization'] = `Bearer ${await user.getIdToken()}`;
  
  const res = await fetch(`${API_BASE_URL}/api/excluded-items`, {
    method: 'POST',
    headers,
    body: JSON.stringify(item),
  });
  
  if (!res.ok) throw new Error('見積もり外項目の作成に失敗しました');
  return res.json();
};

// 見積もり外項目更新（管理者用）
export const updateExcludedItem = async (itemId, item) => {
  const user = auth.currentUser;
  const headers = {
    'Content-Type': 'application/json',
  };
  if (user) headers['Authorization'] = `Bearer ${await user.getIdToken()}`;
  
  const res = await fetch(`${API_BASE_URL}/api/excluded-items/${itemId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(item),
  });
  
  if (!res.ok) throw new Error('見積もり外項目の更新に失敗しました');
  return res.json();
};

// 見積もり外項目削除（管理者用）
export const deleteExcludedItem = async (itemId) => {
  const user = auth.currentUser;
  const headers = {};
  if (user) headers['Authorization'] = `Bearer ${await user.getIdToken()}`;
  
  const res = await fetch(`${API_BASE_URL}/api/excluded-items/${itemId}`, {
    method: 'DELETE',
    headers,
  });
  
  if (!res.ok) throw new Error('見積もり外項目の削除に失敗しました');
  return res.json();
};